/**
 * time-exam — 時間考卷線上互動卷 + 純計分函式。
 *
 * 對應 stats-table-exam.ts / math-exam.ts 同一套形狀:分大題作答 → 交卷即時計分 +
 * 逐題對錯 + 正解 + 錯題回流。純計分 `scoreTimeExam` / 答案正規化 `normalizeTimeAnswer`
 * 抽出可單測(無 DOM 依賴)。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕。
 * 專業介面鐵則:無音效、無 mascot、無彩光、無戲謔文案。
 *
 * 學習/遊戲分離:純學習側,只動學習數據,**絕不**碰 commanderXp / 戰鬥數值。
 */

import type { TimeExam, TimeSection, TimeQuestion } from './time-exam-gen';
import { pageShell, academicButton } from '../ui/components';

// ───────────────────────────── 答案正規化 + 比對 ─────────────────────────────

/**
 * 正規化一個時間答案字串以利寬鬆比對:
 *   - 全形數字 → 半形、全形冒號 / 標點 → 半形。
 *   - 去所有空白。
 *   - 小寫化(英數;AM/PM → am/pm)。
 * 例:'8:30 AM' / '8:30am' / '８:30ＡＭ' → '8:30am';'下午 3 時' → '下午3時'。
 */
export function normalizeTimeAnswer(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();
  // 全形數字 → 半形。
  s = s.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 0x30));
  // 全形英文字母 → 半形(ＡＭ → AM,供 AM/PM 比對;toLowerCase 對全形無效)。
  s = s.replace(/[Ａ-Ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff21 + 0x41));
  s = s.replace(/[ａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff41 + 0x61));
  // 全形 / 中文冒號 → 半形冒號。
  s = s.replace(/[：﹕]/g, ':');
  // 全形空白 → 半形(待會一律去空白)。
  s = s.replace(/　/g, ' ');
  // 去所有空白 + 小寫。
  s = s.replace(/\s+/g, '').toLowerCase();
  return s;
}

/**
 * 一格是否答對(寬鬆比對):
 *   1. 正規化後完全相符 → 對。
 *   2. 「24 小時」== 「24」== 「24小時」(概念題)。
 *   3. hh:mm 寬鬆:'09:00' == '9:00'(去前導 0 比)。
 *   4. AM/PM 寬鬆:'8:30am' == '8:30am'(正規化已小寫去空白);亦接受去前導 0。
 */
export function isTimeAnswerCorrect(studentRaw: string | undefined, correct: string): boolean {
  if (studentRaw === undefined) return false;
  const s = normalizeTimeAnswer(studentRaw);
  const c = normalizeTimeAnswer(correct);
  if (s === '') return false;
  if (s === c) return true;
  // 概念題「24 小時」:接受 '24' / '24小時' / '24時'。
  if (c === '24小時' && (s === '24' || s === '24小時' || s === '24時')) return true;
  // hh:mm / h:mm AM/PM 去前導 0 比(09:00 == 9:00、08:30am == 8:30am)。
  if (stripLeadingZeroHour(s) === stripLeadingZeroHour(c)) return true;
  return false;
}

/** 去掉 'hh:mm' 開頭小時的前導 0('09:00'→'9:00'、'08:30am'→'8:30am')。非時刻格式原樣回。 */
function stripLeadingZeroHour(s: string): string {
  return s.replace(/^0(\d:)/, '$1');
}

// ───────────────────────────── 純計分 ─────────────────────────────

/** 學生整卷作答:`"sectionIdx:questionIdx" → 字串`。 */
export type TimeExamAnswers = Record<string, string>;

/** 單題判定結果。 */
export interface TimeItemResult {
  itemId: string;
  correct: boolean;
  /** 正解顯示字串。 */
  correctLabel: string;
  /** 學生作答顯示字串(未作答 = 空)。 */
  chosenLabel: string;
  points: number;
  earned: number;
}

/** 整卷計分結果。 */
export interface TimeExamScore {
  score: number;
  total: number;
  correctCount: number;
  itemCount: number;
  results: TimeItemResult[];
}

/**
 * 純計分:逐題批改(每題一格)。可單測(無 DOM 依賴)。
 *
 * @param exam    一份時間考卷。
 * @param answers 整卷作答(`"sectionIdx:questionIdx" → 字串`)。
 * @returns 得分 / 滿分 / 逐題結果。
 */
export function scoreTimeExam(exam: TimeExam, answers: TimeExamAnswers): TimeExamScore {
  const results: TimeItemResult[] = [];
  let score = 0;
  let total = 0;
  let correctCount = 0;
  let itemCount = 0;

  exam.sections.forEach((section, sIdx) => {
    section.questions.forEach((q, qIdx) => {
      itemCount += 1;
      total += section.pointsEach;
      const studentRaw = answers[`${sIdx}:${qIdx}`];
      const ok = isTimeAnswerCorrect(studentRaw, q.answer);
      const earned = ok ? section.pointsEach : 0;
      if (ok) {
        correctCount += 1;
        score += section.pointsEach;
      }
      results.push({
        itemId: q.id,
        correct: ok,
        correctLabel: q.answer,
        chosenLabel: studentRaw && studentRaw.trim() !== '' ? studentRaw.trim() : '',
        points: section.pointsEach,
        earned,
      });
    });
  });

  return { score, total, correctCount, itemCount, results };
}

// ───────────────────────────── 互動卷 UI ─────────────────────────────

/** 交卷結果回呼:錯題(供回流錯題本)。 */
export interface TimeExamWrong {
  question: TimeQuestion;
  sectionKind: TimeSection['kind'];
}

/**
 * Render 線上時間考卷 into `container`。每大題列題 + 填答格 → 「交卷」即時計分 + 逐題對錯。
 *
 * @param container 掛載點。
 * @param exam      一份(產生)時間考卷。
 * @param onDone    離開 / 完成回呼。
 * @param onSubmit  選填:交卷後回呼(帶分數 + 錯題),供錯題回流。
 */
export function runTimeExam(
  container: HTMLElement,
  exam: TimeExam,
  onDone: () => void,
  onSubmit?: (score: TimeExamScore, wrong: TimeExamWrong[]) => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: `${exam.scope} · 在每格寫出答案,作答後按「交卷」看分數與正解。`,
    onBack: onDone,
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'time-exam';
  root.setAttribute('data-testid', 'time-exam');
  root.style.cssText = 'display:flex;flex-direction:column;gap:22px;';
  shell.body.appendChild(root);

  const answers: TimeExamAnswers = {};
  let submitted = false;

  exam.sections.forEach((section, sIdx) => {
    const sectionEl = document.createElement('section');
    sectionEl.className = `tm-section tm-section--${section.kind}`;
    sectionEl.setAttribute('data-testid', `tm-section-${section.kind}`);
    sectionEl.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

    const head = document.createElement('div');
    head.className = 'tm-section-head';
    head.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:18px;color:var(--ef-ink);' +
      'border-left:4px solid var(--ef-accent,#2f6b4f);padding-left:10px;';
    head.textContent = `${section.label}、${section.title}`;
    sectionEl.appendChild(head);

    const instr = document.createElement('div');
    instr.className = 'tm-section-instr';
    instr.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
    instr.textContent = section.instructions;
    sectionEl.appendChild(instr);

    // 列題 + 填答格。
    section.questions.forEach((q, qIdx) => {
      sectionEl.appendChild(renderQuestion(sIdx, qIdx, q, answers));
    });

    root.appendChild(sectionEl);
  });

  // ── 交卷 + 結果區 ──
  const actions = document.createElement('div');
  actions.className = 'tm-actions';
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:8px;';
  root.appendChild(actions);

  const resultEl = document.createElement('div');
  resultEl.className = 'tm-result';
  resultEl.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  root.appendChild(resultEl);

  const submitBtn = academicButton({
    label: '交卷',
    variant: 'primary',
    onClick: () => {
      if (submitted) return;
      submitted = true;
      const result = scoreTimeExam(exam, answers);
      renderResult(resultEl, result);
      submitBtn.disabled = true;
      submitBtn.classList.add('tm-submit--done');
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (onSubmit) {
        const wrong: TimeExamWrong[] = [];
        let flat = 0;
        exam.sections.forEach((section) => {
          section.questions.forEach((q) => {
            const r = result.results[flat];
            flat += 1;
            if (r && !r.correct) wrong.push({ question: q, sectionKind: section.kind });
          });
        });
        onSubmit(result, wrong);
      }
    },
  });
  submitBtn.classList.add('tm-submit');
  submitBtn.setAttribute('data-testid', 'tm-submit');
  actions.appendChild(submitBtn);

  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      onDone();
    },
  });
  doneBtn.classList.add('tm-done');
  actions.appendChild(doneBtn);
}

/** 一題的容器(題號 + 題幹 + 一格輸入)。 */
function renderQuestion(
  sIdx: number,
  qIdx: number,
  q: TimeQuestion,
  answers: TimeExamAnswers,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'tm-q';
  row.dataset.itemId = q.id;
  row.style.cssText =
    'display:flex;flex-direction:column;gap:8px;padding:12px 14px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);';

  const top = document.createElement('div');
  top.style.cssText = 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';
  const num = document.createElement('span');
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${qIdx + 1}.`;
  top.appendChild(num);
  const prompt = document.createElement('span');
  prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);line-height:1.6;';
  prompt.textContent = q.prompt;
  top.appendChild(prompt);
  row.appendChild(top);

  const field = document.createElement('label');
  field.style.cssText = 'display:inline-flex;align-items:center;gap:6px;font-size:14px;color:var(--ef-sub);';
  const tag = document.createElement('span');
  tag.textContent = '答:';
  field.appendChild(tag);
  const input = document.createElement('input');
  input.type = 'text';
  input.className = `tm-input tm-input--${q.id}`;
  input.autocomplete = 'off';
  input.style.cssText =
    'min-width:8em;padding:6px 8px;font-size:15px;border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:6px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);';
  answers[`${sIdx}:${qIdx}`] = '';
  input.addEventListener('input', () => {
    answers[`${sIdx}:${qIdx}`] = input.value;
  });
  field.appendChild(input);
  row.appendChild(field);

  return row;
}

/** 結果區:總分 + 逐題對錯 + 正解。 */
function renderResult(el: HTMLElement, result: TimeExamScore): void {
  el.innerHTML = '';
  el.style.display = 'flex';

  const headline = document.createElement('div');
  headline.className = 'tm-score';
  headline.setAttribute('data-testid', 'tm-score');
  headline.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:24px;color:var(--ef-ink);' +
    'border-top:2px solid var(--ef-line,#d8d2c4);padding-top:14px;';
  headline.textContent = `分數:${result.score} / ${result.total}(答對 ${result.correctCount} / ${result.itemCount} 題)`;
  el.appendChild(headline);

  const list = document.createElement('div');
  list.className = 'tm-result-list';
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:14px;';
  result.results.forEach((r, idx) => {
    const line = document.createElement('div');
    line.className = `tm-result-line ${r.correct ? 'tm-correct' : 'tm-wrong'}`;
    const mark = r.correct ? '✓' : '✗';
    const chosen = r.chosenLabel || '(未作答)';
    line.style.color = r.correct ? 'var(--ef-ink)' : '#b03030';
    line.textContent = r.correct
      ? `${mark} 第 ${idx + 1} 題:${r.correctLabel}`
      : `${mark} 第 ${idx + 1} 題:你寫「${chosen}」→ 正解「${r.correctLabel}」`;
    list.appendChild(line);
  });
  el.appendChild(list);
}
